/**
 * Central query-key factory (frontend-map §3.3). Shared, append-only.
 *
 * Foundation seeds the `me`, `masterLists`, and `activities` namespaces UP FRONT
 * so feature agents (library-UI, activities-UI) never need to edit this file in
 * parallel. New keys are added here by foundation/coordination, not by two
 * features at once.
 */
export const queryKeys = {
  me: ['me'] as const,
  mePreferences: ['me', 'preferences'] as const,
  admin: {
    users: ['admin', 'users'] as const,
  },
  masterLists: {
    all: ['master-lists'] as const,
    list: () => ['master-lists', 'list'] as const,
    shared: () => ['master-lists', 'shared'] as const,
    detail: (listId: string) => ['master-lists', 'detail', listId] as const,
    items: (listId: string) => ['master-lists', 'detail', listId, 'items'] as const,
    shares: (listId: string) => ['master-lists', 'detail', listId, 'shares'] as const,
  },
  activities: {
    all: ['activities'] as const,
    list: () => ['activities', 'list'] as const,
    detail: (activityId: string) => ['activities', 'detail', activityId] as const,
  },
  trips: {
    all: ['trips'] as const,
    list: (status?: string) => ['trips', 'list', status ?? 'all'] as const,
    detail: (tripId: string) => ['trips', 'detail', tripId] as const,
    items: (tripId: string) => ['trips', 'detail', tripId, 'items'] as const,
    removedItems: (tripId: string) => ['trips', 'detail', tripId, 'removed-items'] as const,
    comments: (tripId: string) => ['trips', 'detail', tripId, 'comments'] as const,
    members: (tripId: string) => ['trips', 'detail', tripId, 'members'] as const,
    lessons: (tripId: string) => ['trips', 'detail', tripId, 'lessons'] as const,
    labels: (tripId: string) => ['trips', 'detail', tripId, 'labels'] as const,
    questions: (tripId: string) => ['trips', 'detail', tripId, 'questions'] as const,
    collaborators: (tripId: string) => ['trips', 'detail', tripId, 'collaborators'] as const,
    invites: (tripId: string) => ['trips', 'detail', tripId, 'invites'] as const,
    incomingInvites: ['trips', 'invites', 'incoming'] as const,
    events: (tripId: string) => ['trips', 'detail', tripId, 'events'] as const,
    itemEvents: (tripId: string, itemId: string) =>
      ['trips', 'detail', tripId, 'items', itemId, 'events'] as const,
    itemComments: (tripId: string, itemId: string) =>
      ['trips', 'detail', tripId, 'items', itemId, 'comments'] as const,
  },
  lessons: {
    detail: (lessonId: string) => ['lessons', 'detail', lessonId] as const,
    masterList: (listId: string, status?: string) =>
      ['lessons', 'master-list', listId, status ?? 'all'] as const,
  },
} as const;
